/**
 * FinanceApp - Module Agenda & Prise de RDV (type Calendly)
 * Copyright (c) 2026 Emmanuel TESTE
 */
import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useToast } from "../contexts/ToastContext";
import api from "../services/api";
import {
  Calendar, List, Settings, Clock, BarChart2, Plus, Edit2, Trash2,
  Copy, Check, Video, Phone, MapPin, Shuffle, Eye, Mail, X, ChevronLeft,
  ChevronRight, Link2, AlertCircle, CheckCircle, XCircle, Clock3,
  UserCheck, RefreshCw,
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell,
} from "recharts";

const TABS = [
  { id: "calendar",  label: "Calendrier",     Icon: Calendar },
  { id: "list",      label: "Liste RDV",       Icon: List },
  { id: "types",     label: "Types de RDV",    Icon: Settings },
  { id: "avails",    label: "Disponibilités",  Icon: Clock },
  { id: "stats",     label: "Statistiques",    Icon: BarChart2 },
];

const STATUS_LABELS = {
  en_attente: { label: "En attente",  color: "#F59E0B" },
  confirmé:   { label: "Confirmé",    color: "#10B981" },
  annulé:     { label: "Annulé",      color: "#EF4444" },
  reporté:    { label: "Reporté",     color: "#6366F1" },
  terminé:    { label: "Terminé",     color: "#64748B" },
  no_show:    { label: "No-show",     color: "#DC2626" },
};

const LOCATION_ICONS = {
  visio:      { Icon: Video,  label: "Visio" },
  telephone:  { Icon: Phone,  label: "Téléphone" },
  presentiel: { Icon: MapPin, label: "Présentiel" },
  flexible:   { Icon: Shuffle,label: "Flexible" },
};

const DAYS = ["Dim", "Lun", "Mar", "Mer", "Jeu", "Ven", "Sam"];
const DAYS_FULL = ["Dimanche","Lundi","Mardi","Mercredi","Jeudi","Vendredi","Samedi"];

const card = {
  background: "var(--bg-card)",
  border: "1px solid var(--border)",
  borderRadius: 12,
  padding: 20,
};

const BASE_URL = typeof window !== "undefined" ? window.location.origin : "http://localhost:3000";

// ── Helpers ────────────────────────────────────────────────────────────────

function formatDT(dt) {
  if (!dt) return "—";
  return new Intl.DateTimeFormat("fr-FR", {
    weekday: "short", day: "numeric", month: "short",
    hour: "2-digit", minute: "2-digit",
  }).format(new Date(dt));
}

function formatDate(dt) {
  if (!dt) return "—";
  return new Intl.DateTimeFormat("fr-FR", {
    day: "numeric", month: "long", year: "numeric",
  }).format(new Date(dt));
}

// ── Composant Modal ────────────────────────────────────────────────────────

function Modal({ open, onClose, title, children, wide }) {
  if (!open) return null;
  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 1000,
        background: "rgba(0,0,0,0.6)", display: "flex",
        alignItems: "center", justifyContent: "center", padding: 16,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: "var(--bg-card)", borderRadius: 16,
          border: "1px solid var(--border)", padding: 24,
          width: "100%", maxWidth: wide ? 760 : 520,
          maxHeight: "90vh", overflowY: "auto",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
          <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: "var(--text-primary)" }}>{title}</h3>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-muted)", padding: 4 }}>
            <X size={18} />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

// ── Input helper ────────────────────────────────────────────────────────────

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "var(--text-muted)", marginBottom: 5 }}>{label}</label>
      {children}
    </div>
  );
}

const inputStyle = {
  width: "100%", padding: "8px 12px", borderRadius: 8,
  border: "1px solid var(--border)", background: "var(--bg-primary)",
  color: "var(--text-primary)", fontSize: 13, boxSizing: "border-box",
};

const btnPrimary = {
  background: "#6366F1", color: "#fff", border: "none", borderRadius: 8,
  padding: "9px 18px", cursor: "pointer", fontWeight: 600, fontSize: 13,
};

const btnSecondary = {
  background: "transparent", color: "var(--text-primary)",
  border: "1px solid var(--border)", borderRadius: 8,
  padding: "9px 18px", cursor: "pointer", fontWeight: 500, fontSize: 13,
};

const btnDanger = {
  background: "rgba(239,68,68,0.1)", color: "#EF4444",
  border: "1px solid rgba(239,68,68,0.25)", borderRadius: 8,
  padding: "7px 14px", cursor: "pointer", fontWeight: 500, fontSize: 12,
};

// ── Tab 1 : Calendrier ─────────────────────────────────────────────────────

function CalendarTab({ appointments, onApptClick }) {
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(new Date().getMonth());

  const today = new Date();
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const MONTH_NAMES = ["Janvier","Février","Mars","Avril","Mai","Juin","Juillet","Août","Septembre","Octobre","Novembre","Décembre"];

  const apptsByDate = {};
  for (const a of appointments) {
    const key = a.start_datetime?.slice(0, 10);
    if (!apptsByDate[key]) apptsByDate[key] = [];
    apptsByDate[key].push(a);
  }

  const prevMonth = () => { if (month === 0) { setMonth(11); setYear(y => y - 1); } else setMonth(m => m - 1); };
  const nextMonth = () => { if (month === 11) { setMonth(0); setYear(y => y + 1); } else setMonth(m => m + 1); };

  const blanks = firstDay === 0 ? 6 : firstDay - 1; // Lundi en premier

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
        <button onClick={prevMonth} style={{ ...btnSecondary, padding: "7px 12px" }}><ChevronLeft size={16} /></button>
        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: "var(--text-primary)" }}>
          {MONTH_NAMES[month]} {year}
        </h3>
        <button onClick={nextMonth} style={{ ...btnSecondary, padding: "7px 12px" }}><ChevronRight size={16} /></button>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 4, marginBottom: 4 }}>
        {["Lun","Mar","Mer","Jeu","Ven","Sam","Dim"].map(d => (
          <div key={d} style={{ textAlign: "center", fontSize: 11, fontWeight: 700, color: "var(--text-muted)", padding: "4px 0" }}>{d}</div>
        ))}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 4 }}>
        {Array.from({ length: blanks }).map((_, i) => <div key={`b${i}`} />)}
        {Array.from({ length: daysInMonth }).map((_, i) => {
          const day = i + 1;
          const dateStr = `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
          const isToday = today.getFullYear() === year && today.getMonth() === month && today.getDate() === day;
          const dayAppts = apptsByDate[dateStr] || [];

          return (
            <div
              key={day}
              style={{
                minHeight: 72, padding: 6, borderRadius: 8,
                background: isToday ? "rgba(99,102,241,0.12)" : "rgba(255,255,255,0.02)",
                border: isToday ? "1px solid rgba(99,102,241,0.4)" : "1px solid var(--border)",
                cursor: dayAppts.length > 0 ? "pointer" : "default",
              }}
            >
              <div style={{
                fontSize: 12, fontWeight: isToday ? 700 : 400,
                color: isToday ? "#818CF8" : "var(--text-muted)",
                marginBottom: 4,
              }}>{day}</div>
              {dayAppts.slice(0, 3).map(a => (
                <div
                  key={a.id}
                  onClick={() => onApptClick(a)}
                  style={{
                    fontSize: 10, padding: "2px 5px", borderRadius: 4, marginBottom: 2,
                    background: a.type_color || "#6366F1", color: "#fff",
                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                    cursor: "pointer",
                  }}
                  title={`${a.guest_name} — ${a.type_title}`}
                >
                  {a.start_datetime?.slice(11, 16)} {a.guest_name?.split(" ")[0]}
                </div>
              ))}
              {dayAppts.length > 3 && (
                <div style={{ fontSize: 9, color: "var(--text-muted)" }}>+{dayAppts.length - 3}</div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Tab 2 : Liste RDV ──────────────────────────────────────────────────────

function ListTab({ appointments, meetingTypes, onUpdate, onCancel }) {
  const [statusFilter, setStatusFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [selectedAppt, setSelectedAppt] = useState(null);

  const filtered = appointments.filter(a =>
    (!statusFilter || a.status === statusFilter) &&
    (!typeFilter || String(a.meeting_type_id) === typeFilter)
  );

  return (
    <div>
      {/* Filtres */}
      <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap" }}>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={{ ...inputStyle, width: "auto", flex: "0 0 auto" }}>
          <option value="">Tous les statuts</option>
          {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)} style={{ ...inputStyle, width: "auto", flex: "0 0 auto" }}>
          <option value="">Tous les types</option>
          {meetingTypes.map(t => <option key={t.id} value={t.id}>{t.title}</option>)}
        </select>
      </div>

      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr style={{ borderBottom: "1px solid var(--border)" }}>
              {["Date", "Invité", "Type", "Statut", "Confirmé", "Actions"].map(h => (
                <th key={h} style={{ padding: "8px 12px", textAlign: "left", fontSize: 11, fontWeight: 700, color: "var(--text-muted)", textTransform: "uppercase" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={6} style={{ padding: 32, textAlign: "center", color: "var(--text-muted)" }}>Aucun rendez-vous</td></tr>
            ) : filtered.map(a => {
              const st = STATUS_LABELS[a.status] || { label: a.status, color: "#64748B" };
              return (
                <tr key={a.id} style={{ borderBottom: "1px solid var(--border)" }} onMouseEnter={e => e.currentTarget.style.background = "rgba(255,255,255,0.03)"} onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                  <td style={{ padding: "10px 12px", color: "var(--text-primary)" }}>
                    <div style={{ fontWeight: 600 }}>{a.start_datetime?.slice(0, 10)}</div>
                    <div style={{ fontSize: 11, color: "var(--text-muted)" }}>{a.start_datetime?.slice(11, 16)}</div>
                  </td>
                  <td style={{ padding: "10px 12px" }}>
                    <div style={{ fontWeight: 600, color: "var(--text-primary)" }}>{a.guest_name}</div>
                    <div style={{ fontSize: 11, color: "var(--text-muted)" }}>{a.guest_email}</div>
                    {a.guest_company && <div style={{ fontSize: 11, color: "var(--text-muted)" }}>{a.guest_company}</div>}
                  </td>
                  <td style={{ padding: "10px 12px" }}>
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
                      <span style={{ width: 8, height: 8, borderRadius: "50%", background: a.type_color || "#6366F1", display: "inline-block" }} />
                      <span style={{ color: "var(--text-primary)", fontSize: 12 }}>{a.type_title}</span>
                    </span>
                  </td>
                  <td style={{ padding: "10px 12px" }}>
                    <span style={{ background: `${st.color}20`, color: st.color, padding: "3px 8px", borderRadius: 6, fontSize: 11, fontWeight: 600 }}>{st.label}</span>
                  </td>
                  <td style={{ padding: "10px 12px", fontSize: 11, color: "var(--text-muted)" }}>
                    {a.confirmed_at ? "✅ " + a.confirmed_at?.slice(0, 10) : "—"}
                  </td>
                  <td style={{ padding: "10px 12px" }}>
                    <div style={{ display: "flex", gap: 6 }}>
                      <button onClick={() => setSelectedAppt(a)} title="Voir détail" style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-muted)", padding: 4 }}><Eye size={14} /></button>
                      {a.status !== "annulé" && a.status !== "terminé" && (
                        <button onClick={() => onCancel(a.id)} title="Annuler" style={{ background: "none", border: "none", cursor: "pointer", color: "#EF4444", padding: 4 }}><XCircle size={14} /></button>
                      )}
                      {a.status === "en_attente" && (
                        <button onClick={() => onUpdate(a.id, { status: "confirmé" })} title="Confirmer" style={{ background: "none", border: "none", cursor: "pointer", color: "#10B981", padding: 4 }}><CheckCircle size={14} /></button>
                      )}
                      {a.status === "confirmé" && (
                        <button onClick={() => onUpdate(a.id, { status: "terminé" })} title="Marquer terminé" style={{ background: "none", border: "none", cursor: "pointer", color: "#64748B", padding: 4 }}><UserCheck size={14} /></button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Modal détail RDV */}
      <Modal open={!!selectedAppt} onClose={() => setSelectedAppt(null)} title="Détail du rendez-vous">
        {selectedAppt && (
          <div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
              {[
                ["Invité", selectedAppt.guest_name],
                ["Email", selectedAppt.guest_email],
                ["Téléphone", selectedAppt.guest_phone || "—"],
                ["Société", selectedAppt.guest_company || "—"],
                ["Date", formatDT(selectedAppt.start_datetime)],
                ["Type", selectedAppt.type_title],
                ["Statut", STATUS_LABELS[selectedAppt.status]?.label || selectedAppt.status],
                ["Message", selectedAppt.guest_message || "—"],
              ].map(([k, v]) => (
                <div key={k} style={{ background: "rgba(255,255,255,0.03)", borderRadius: 8, padding: "8px 12px" }}>
                  <div style={{ fontSize: 10, color: "var(--text-muted)", fontWeight: 700, textTransform: "uppercase", marginBottom: 3 }}>{k}</div>
                  <div style={{ fontSize: 13, color: "var(--text-primary)" }}>{v}</div>
                </div>
              ))}
            </div>
            <Field label="Notes internes">
              <textarea
                defaultValue={selectedAppt.internal_notes || ""}
                rows={3}
                style={{ ...inputStyle, resize: "vertical" }}
                onBlur={e => onUpdate(selectedAppt.id, { internal_notes: e.target.value })}
              />
            </Field>
          </div>
        )}
      </Modal>
    </div>
  );
}

// ── Tab 3 : Types de RDV ───────────────────────────────────────────────────

function TypesTab({ types, onSave, onDelete }) {
  const [showForm, setShowForm] = useState(false);
  const [editType, setEditType] = useState(null);
  const [copied, setCopied] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    title: "", description: "", duration_minutes: 30, color: "#6366F1",
    location_type: "visio", location_details: "", is_active: true,
    min_notice_hours: 24, max_days_ahead: 60, buffer_minutes: 0,
  });

  const openNew = () => {
    setEditType(null);
    setForm({ title: "", description: "", duration_minutes: 30, color: "#6366F1", location_type: "visio", location_details: "", is_active: true, min_notice_hours: 24, max_days_ahead: 60, buffer_minutes: 0 });
    setShowForm(true);
  };

  const openEdit = (t) => {
    setEditType(t);
    setForm({ title: t.title, description: t.description || "", duration_minutes: t.duration_minutes, color: t.color, location_type: t.location_type, location_details: t.location_details || "", is_active: t.is_active === 1, min_notice_hours: t.min_notice_hours, max_days_ahead: t.max_days_ahead, buffer_minutes: t.buffer_minutes });
    setShowForm(true);
  };

  const handleSave = async () => {
    if (saving) return;
    setSaving(true);
    try {
      await onSave(form, editType?.id);
      setShowForm(false);
    } finally {
      setSaving(false);
    }
  };

  const copyLink = (url) => {
    navigator.clipboard.writeText(url);
    setCopied(url);
    setTimeout(() => setCopied(null), 2000);
  };

  const SUGGESTED = [
    { title: "Démo FinanceApp", duration_minutes: 45, location_type: "visio", color: "#6366F1", description: "Découvrez FinanceApp en 45 minutes." },
    { title: "Support technique", duration_minutes: 30, location_type: "visio", color: "#10B981", description: "Résolvons votre problème ensemble." },
    { title: "Réunion commerciale", duration_minutes: 60, location_type: "flexible", color: "#F59E0B", description: "Discutons de votre projet." },
    { title: "Appel découverte", duration_minutes: 15, location_type: "telephone", color: "#8B5CF6", description: "Un premier échange rapide." },
  ];

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: "var(--text-primary)" }}>Types de rendez-vous</h3>
        <button onClick={openNew} style={btnPrimary}><Plus size={14} style={{ marginRight: 6 }} />Nouveau type</button>
      </div>

      {types.length === 0 && (
        <div>
          <p style={{ color: "var(--text-muted)", fontSize: 13, marginBottom: 16 }}>Commencez par créer des types de RDV ou choisissez parmi les suggestions :</p>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(200px,1fr))", gap: 12 }}>
            {SUGGESTED.map((s, i) => (
              <div
                key={i}
                onClick={() => { setForm({ ...form, ...s, is_active: true, min_notice_hours: 24, max_days_ahead: 60, buffer_minutes: 0, location_details: "" }); setEditType(null); setShowForm(true); }}
                style={{ ...card, cursor: "pointer", borderLeft: `4px solid ${s.color}` }}
              >
                <div style={{ fontWeight: 700, fontSize: 13, color: "var(--text-primary)", marginBottom: 4 }}>{s.title}</div>
                <div style={{ fontSize: 11, color: "var(--text-muted)" }}>{s.duration_minutes} min · {LOCATION_ICONS[s.location_type]?.label}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(280px,1fr))", gap: 16 }}>
        {types.map(t => {
          const LocIcon = LOCATION_ICONS[t.location_type]?.Icon;
          const bookingUrl = t.booking_url || `${BASE_URL}/booking/${t.slug}`;
          return (
            <div key={t.id} style={{ ...card, borderLeft: `4px solid ${t.color}`, opacity: t.is_active ? 1 : 0.6 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 14, color: "var(--text-primary)" }}>{t.title}</div>
                  <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 2 }}>
                    {t.duration_minutes} min · {LocIcon && <LocIcon size={11} style={{ verticalAlign: "middle" }} />} {LOCATION_ICONS[t.location_type]?.label}
                  </div>
                </div>
                <div style={{ display: "flex", gap: 6 }}>
                  <button onClick={() => openEdit(t)} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-muted)" }}><Edit2 size={14} /></button>
                  <button onClick={() => onDelete(t.id)} style={{ background: "none", border: "none", cursor: "pointer", color: "#EF4444" }}><Trash2 size={14} /></button>
                </div>
              </div>
              {t.description && <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 12, lineHeight: 1.5 }}>{t.description}</div>}
              <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                <button
                  onClick={() => copyLink(bookingUrl)}
                  style={{ display: "flex", alignItems: "center", gap: 5, background: "rgba(99,102,241,0.1)", color: "#818CF8", border: "1px solid rgba(99,102,241,0.2)", borderRadius: 6, padding: "5px 10px", cursor: "pointer", fontSize: 11, fontWeight: 600 }}
                >
                  {copied === bookingUrl ? <Check size={12} /> : <Copy size={12} />}
                  {copied === bookingUrl ? "Copié !" : "Copier le lien"}
                </button>
                <a
                  href={`/booking/${t.slug}`}
                  target="_blank"
                  rel="noreferrer"
                  style={{ display: "flex", alignItems: "center", gap: 5, background: "rgba(255,255,255,0.05)", color: "var(--text-muted)", border: "1px solid var(--border)", borderRadius: 6, padding: "5px 10px", textDecoration: "none", fontSize: 11 }}
                >
                  <Eye size={12} /> Aperçu
                </a>
              </div>
              <div style={{ marginTop: 8, fontSize: 10, color: "var(--text-muted)", fontFamily: "monospace", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {bookingUrl}
              </div>
            </div>
          );
        })}
      </div>

      {/* Modal formulaire */}
      <Modal open={showForm} onClose={() => setShowForm(false)} title={editType ? "Modifier le type" : "Nouveau type de RDV"} wide>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <div style={{ gridColumn: "1 / -1" }}>
            <Field label="Titre *">
              <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="Ex : Démo FinanceApp" style={inputStyle} />
            </Field>
          </div>
          <div style={{ gridColumn: "1 / -1" }}>
            <Field label="Description">
              <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} rows={2} style={{ ...inputStyle, resize: "vertical" }} />
            </Field>
          </div>
          <Field label="Durée (minutes)">
            <select value={form.duration_minutes} onChange={e => setForm(f => ({ ...f, duration_minutes: +e.target.value }))} style={inputStyle}>
              {[15, 30, 45, 60, 90].map(d => <option key={d} value={d}>{d} min</option>)}
            </select>
          </Field>
          <Field label="Type de lieu">
            <select value={form.location_type} onChange={e => setForm(f => ({ ...f, location_type: e.target.value }))} style={inputStyle}>
              <option value="visio">📹 Visio</option>
              <option value="telephone">📞 Téléphone</option>
              <option value="presentiel">📍 Présentiel</option>
              <option value="flexible">🔀 Flexible</option>
            </select>
          </Field>
          <div style={{ gridColumn: "1 / -1" }}>
            <Field label="Lien / adresse">
              <input value={form.location_details} onChange={e => setForm(f => ({ ...f, location_details: e.target.value }))} placeholder="Lien Zoom/Meet ou adresse..." style={inputStyle} />
            </Field>
          </div>
          <Field label="Couleur">
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <input type="color" value={form.color} onChange={e => setForm(f => ({ ...f, color: e.target.value }))} style={{ width: 40, height: 36, border: "none", background: "none", cursor: "pointer" }} />
              <span style={{ fontSize: 12, color: "var(--text-muted)" }}>{form.color}</span>
            </div>
          </Field>
          <Field label="Délai minimum (heures)">
            <input type="number" value={form.min_notice_hours} onChange={e => setForm(f => ({ ...f, min_notice_hours: +e.target.value }))} min={0} style={inputStyle} />
          </Field>
          <Field label="Horizon max (jours)">
            <input type="number" value={form.max_days_ahead} onChange={e => setForm(f => ({ ...f, max_days_ahead: +e.target.value }))} min={1} style={inputStyle} />
          </Field>
          <Field label="Tampon entre RDV (min)">
            <input type="number" value={form.buffer_minutes} onChange={e => setForm(f => ({ ...f, buffer_minutes: +e.target.value }))} min={0} style={inputStyle} />
          </Field>
        </div>
        <div style={{ display: "flex", gap: 10, marginTop: 20, justifyContent: "flex-end" }}>
          <button onClick={() => setShowForm(false)} style={btnSecondary}>Annuler</button>
          <button onClick={handleSave} style={{ ...btnPrimary, opacity: (!form.title || saving) ? 0.6 : 1 }} disabled={!form.title || saving}>
            {saving ? "Enregistrement…" : editType ? "Mettre à jour" : "Créer le type"}
          </button>
        </div>
      </Modal>
    </div>
  );
}

// ── Tab 4 : Disponibilités ─────────────────────────────────────────────────

function AvailsTab({ availabilities, exceptions, onSaveDay, onDeleteAvail, onAddException, onDeleteException }) {
  const [showExcForm, setShowExcForm] = useState(false);
  const [excForm, setExcForm] = useState({ exception_date: "", reason: "", is_unavailable: true });
  const [editingDay, setEditingDay] = useState(null);
  const [daySlots, setDaySlots] = useState([]);
  const [savingDay, setSavingDay] = useState(false);

  const byDay = {};
  for (let i = 0; i < 7; i++) {
    byDay[i] = availabilities.filter(a => a.day_of_week === i);
  }

  const openDay = (d) => {
    setEditingDay(d);
    const slots = byDay[d].map(a => ({ start_time: a.start_time, end_time: a.end_time, is_active: a.is_active === 1 }));
    if (slots.length === 0) slots.push({ start_time: "09:00", end_time: "18:00", is_active: true });
    setDaySlots(slots);
  };

  const addSlot = () => setDaySlots(s => [...s, { start_time: "09:00", end_time: "18:00", is_active: true }]);
  const removeSlot = (i) => setDaySlots(s => s.filter((_, idx) => idx !== i));
  const updateSlot = (i, field, val) => setDaySlots(s => s.map((sl, idx) => idx === i ? { ...sl, [field]: val } : sl));

  const saveDay = async () => {
    if (savingDay) return;
    setSavingDay(true);
    try {
      await onSaveDay(editingDay, daySlots);
      setEditingDay(null);
    } finally {
      setSavingDay(false);
    }
  };

  const addException = () => {
    if (!excForm.exception_date) return;
    onAddException(excForm);
    setExcForm({ exception_date: "", reason: "", is_unavailable: true });
    setShowExcForm(false);
  };

  return (
    <div>
      <h3 style={{ margin: "0 0 16px", fontSize: 15, fontWeight: 700, color: "var(--text-primary)" }}>Plages horaires</h3>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(200px,1fr))", gap: 10, marginBottom: 28 }}>
        {[1, 2, 3, 4, 5, 6, 0].map(d => {
          const slots = byDay[d] || [];
          const active = slots.filter(s => s.is_active !== 0);
          return (
            <div key={d} style={{ ...card, cursor: "pointer" }} onClick={() => openDay(d)}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                <div style={{ fontWeight: 700, fontSize: 13, color: "var(--text-primary)" }}>{DAYS_FULL[d]}</div>
                <span style={{
                  fontSize: 10, padding: "2px 7px", borderRadius: 10, fontWeight: 700,
                  background: active.length > 0 ? "rgba(16,185,129,0.15)" : "rgba(239,68,68,0.1)",
                  color: active.length > 0 ? "#10B981" : "#EF4444",
                }}>
                  {active.length > 0 ? "Ouvert" : "Fermé"}
                </span>
              </div>
              {active.map((s, i) => (
                <div key={i} style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 2 }}>
                  {s.start_time} – {s.end_time}
                </div>
              ))}
              {active.length === 0 && <div style={{ fontSize: 12, color: "var(--text-muted)" }}>Indisponible</div>}
            </div>
          );
        })}
      </div>

      {/* Exceptions */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: "var(--text-primary)" }}>Exceptions & congés</h3>
        <button onClick={() => setShowExcForm(true)} style={btnSecondary}><Plus size={13} style={{ marginRight: 5 }} />Ajouter</button>
      </div>
      {exceptions.length === 0 ? (
        <div style={{ color: "var(--text-muted)", fontSize: 13 }}>Aucune exception configurée</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {exceptions.map(e => (
            <div key={e.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 14px", background: "rgba(239,68,68,0.06)", border: "1px solid rgba(239,68,68,0.15)", borderRadius: 8 }}>
              <AlertCircle size={14} color="#EF4444" />
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, fontSize: 13, color: "var(--text-primary)" }}>{formatDate(e.exception_date)}</div>
                {e.reason && <div style={{ fontSize: 11, color: "var(--text-muted)" }}>{e.reason}</div>}
              </div>
              <button onClick={() => onDeleteException(e.id)} style={{ background: "none", border: "none", cursor: "pointer", color: "#EF4444" }}><X size={14} /></button>
            </div>
          ))}
        </div>
      )}

      {/* Modal édition jour */}
      <Modal open={editingDay !== null} onClose={() => setEditingDay(null)} title={`Disponibilités — ${editingDay !== null ? DAYS_FULL[editingDay] : ""}`}>
        <p style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 0 }}>Définissez vos plages horaires pour ce jour. Laissez vide pour marquer comme indisponible.</p>
        {daySlots.map((slot, i) => (
          <div key={i} style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 10 }}>
            <input type="time" value={slot.start_time} onChange={e => updateSlot(i, "start_time", e.target.value)} style={{ ...inputStyle, width: 110 }} />
            <span style={{ color: "var(--text-muted)" }}>–</span>
            <input type="time" value={slot.end_time} onChange={e => updateSlot(i, "end_time", e.target.value)} style={{ ...inputStyle, width: 110 }} />
            <button onClick={() => removeSlot(i)} style={{ background: "none", border: "none", cursor: "pointer", color: "#EF4444" }}><Trash2 size={14} /></button>
          </div>
        ))}
        <button onClick={addSlot} style={{ ...btnSecondary, marginBottom: 20, fontSize: 12, padding: "6px 14px" }}>+ Ajouter une plage</button>
        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <button onClick={() => { setEditingDay(null); }} style={btnSecondary}>Annuler</button>
          <button onClick={saveDay} style={{ ...btnPrimary, opacity: savingDay ? 0.6 : 1 }} disabled={savingDay}>
            {savingDay ? "Enregistrement…" : "Enregistrer"}
          </button>
        </div>
      </Modal>

      {/* Modal ajout exception */}
      <Modal open={showExcForm} onClose={() => setShowExcForm(false)} title="Ajouter une exception">
        <Field label="Date *">
          <input type="date" value={excForm.exception_date} onChange={e => setExcForm(f => ({ ...f, exception_date: e.target.value }))} style={inputStyle} />
        </Field>
        <Field label="Motif (optionnel)">
          <input value={excForm.reason} onChange={e => setExcForm(f => ({ ...f, reason: e.target.value }))} placeholder="Congé, férié..." style={inputStyle} />
        </Field>
        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 16 }}>
          <button onClick={() => setShowExcForm(false)} style={btnSecondary}>Annuler</button>
          <button onClick={addException} style={btnPrimary} disabled={!excForm.exception_date}>Ajouter</button>
        </div>
      </Modal>
    </div>
  );
}

// ── Tab 5 : Statistiques ───────────────────────────────────────────────────

function StatsTab({ stats }) {
  if (!stats) return <div style={{ color: "var(--text-muted)", textAlign: "center", padding: 40 }}>Chargement…</div>;

  const kpis = [
    { label: "RDV ce mois", value: stats.totals?.this_month || 0, color: "#6366F1" },
    { label: "Taux confirmation", value: `${stats.confirmRate || 0}%`, color: "#10B981" },
    { label: "Taux no-show", value: `${stats.noShowRate || 0}%`, color: "#EF4444" },
    { label: "Total RDV", value: stats.totals?.total || 0, color: "#F59E0B" },
  ];

  return (
    <div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(160px,1fr))", gap: 12, marginBottom: 28 }}>
        {kpis.map(k => (
          <div key={k.label} style={{ ...card, borderTop: `3px solid ${k.color}`, textAlign: "center" }}>
            <div style={{ fontSize: 28, fontWeight: 800, color: k.color, marginBottom: 4 }}>{k.value}</div>
            <div style={{ fontSize: 12, color: "var(--text-muted)" }}>{k.label}</div>
          </div>
        ))}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        <div style={card}>
          <h4 style={{ margin: "0 0 14px", fontSize: 13, fontWeight: 700, color: "var(--text-primary)" }}>Types les plus populaires</h4>
          {(stats.byType || []).length === 0 ? (
            <div style={{ color: "var(--text-muted)", fontSize: 12 }}>Aucune donnée</div>
          ) : (
            <div>
              {stats.byType.map((t, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                  <div style={{ width: 10, height: 10, borderRadius: "50%", background: t.color || "#6366F1", flexShrink: 0 }} />
                  <div style={{ flex: 1, fontSize: 12, color: "var(--text-primary)" }}>{t.title}</div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: "var(--text-muted)" }}>{t.count}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div style={card}>
          <h4 style={{ margin: "0 0 14px", fontSize: 13, fontWeight: 700, color: "var(--text-primary)" }}>Heures populaires</h4>
          {(stats.byHour || []).length === 0 ? (
            <div style={{ color: "var(--text-muted)", fontSize: 12 }}>Aucune donnée</div>
          ) : (
            <ResponsiveContainer width="100%" height={160}>
              <BarChart data={stats.byHour}>
                <XAxis dataKey="hour" tick={{ fontSize: 10, fill: "var(--text-muted)" }} tickFormatter={h => `${h}h`} />
                <YAxis hide />
                <Tooltip formatter={(v) => [v, "RDV"]} labelFormatter={h => `${h}h00`} />
                <Bar dataKey="count" fill="#6366F1" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {stats.upcoming?.length > 0 && (
        <div style={{ ...card, marginTop: 16 }}>
          <h4 style={{ margin: "0 0 14px", fontSize: 13, fontWeight: 700, color: "var(--text-primary)" }}>Prochains RDV</h4>
          {stats.upcoming.map(a => (
            <div key={a.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "8px 0", borderBottom: "1px solid var(--border)" }}>
              <div style={{ width: 8, height: 8, borderRadius: "50%", background: a.type_color || "#6366F1", flexShrink: 0 }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text-primary)" }}>{a.guest_name}</div>
                <div style={{ fontSize: 11, color: "var(--text-muted)" }}>{a.type_title}</div>
              </div>
              <div style={{ fontSize: 12, color: "var(--text-muted)" }}>{formatDT(a.start_datetime)}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Composant principal Agenda ─────────────────────────────────────────────

export default function Agenda() {
  const { user } = useAuth();
  const { addToast } = useToast();
  const [activeTab, setActiveTab] = useState("calendar");
  const [meetingTypes, setMeetingTypes] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [availabilities, setAvailabilities] = useState([]);
  const [exceptions, setExceptions] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const authHeader = { Authorization: `Bearer ${localStorage.getItem("financeapp_token")}` };
    const endpoints = [
      { key: "types",  url: "/api/agenda/types" },
      { key: "appts",  url: "/api/agenda/appointments?limit=500" },
      { key: "avails", url: "/api/agenda/availabilities" },
      { key: "exceps", url: "/api/agenda/exceptions" },
      { key: "stats",  url: "/api/agenda/stats" },
    ];
    // Promise.allSettled : un appel en échec ne bloque plus les 4 autres
    // (avec Promise.all précédent, un seul échec empêchait TOUT l'écran de se mettre à jour)
    const results = await Promise.allSettled(
      endpoints.map(e => fetch(e.url, { headers: authHeader }).then(r => {
        if (!r.ok) throw new Error(`${e.key} (${r.status})`);
        return r.json();
      }))
    );

    const byKey = {};
    const failed = [];
    results.forEach((res, i) => {
      const key = endpoints[i].key;
      if (res.status === "fulfilled") byKey[key] = res.value;
      else failed.push(key);
    });

    if (byKey.types  !== undefined) setMeetingTypes(Array.isArray(byKey.types) ? byKey.types : []);
    if (byKey.appts  !== undefined) setAppointments(Array.isArray(byKey.appts?.appointments) ? byKey.appts.appointments : []);
    if (byKey.avails !== undefined) setAvailabilities(Array.isArray(byKey.avails) ? byKey.avails : []);
    if (byKey.exceps !== undefined) setExceptions(Array.isArray(byKey.exceps) ? byKey.exceps : []);
    if (byKey.stats  !== undefined) setStats(byKey.stats);

    if (failed.length > 0) {
      addToast(`Erreur de chargement : ${failed.join(", ")}`, "error");
    }
    setLoading(false);
  }, [addToast]);

  useEffect(() => { load(); }, [load]);

  const authHeaders = { Authorization: `Bearer ${localStorage.getItem("financeapp_token")}`, "Content-Type": "application/json" };

  const saveType = async (form, id) => {
    const url = id ? `/api/agenda/types/${id}` : "/api/agenda/types";
    const method = id ? "PUT" : "POST";
    const r = await fetch(url, { method, headers: authHeaders, body: JSON.stringify(form) });
    if (!r.ok) { addToast("Erreur sauvegarde type", "error"); return; }
    addToast(id ? "Type mis à jour" : "Type créé", "success");
    await load();
  };

  const deleteType = async (id) => {
    if (!window.confirm("Supprimer ce type de RDV ?")) return;
    try {
      const r = await fetch(`/api/agenda/types/${id}`, { method: "DELETE", headers: authHeaders });
      if (!r.ok) {
        const data = await r.json().catch(() => ({}));
        addToast(`Échec de la suppression : ${data.error || r.status}`, "error");
        return;
      }
      addToast("Supprimé", "success");
      await load();
    } catch (e) {
      addToast("Erreur réseau lors de la suppression", "error");
    }
  };

  const updateAppt = async (id, data) => {
    try {
      const r = await fetch(`/api/agenda/appointments/${id}`, { method: "PUT", headers: authHeaders, body: JSON.stringify(data) });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        addToast(`Échec de la mise à jour : ${err.error || r.status}`, "error");
        return;
      }
      addToast("Rendez-vous mis à jour", "success");
      await load();
    } catch (e) {
      addToast("Erreur réseau", "error");
    }
  };

  const cancelAppt = async (id) => {
    if (!window.confirm("Annuler ce rendez-vous ?")) return;
    await updateAppt(id, { status: "annulé" });
  };

  const saveDay = async (day, slots) => {
    try {
      const r = await fetch(`/api/agenda/availabilities/day/${day}`, { method: "PUT", headers: authHeaders, body: JSON.stringify({ slots }) });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        addToast(`Échec de l'enregistrement : ${err.error || r.status}`, "error");
        return;
      }
      addToast("Disponibilités enregistrées", "success");
      await load();
    } catch (e) {
      addToast("Erreur réseau", "error");
    }
  };

  const addException = async (form) => {
    try {
      const r = await fetch("/api/agenda/exceptions", { method: "POST", headers: authHeaders, body: JSON.stringify(form) });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        addToast(`Échec de l'ajout : ${err.error || r.status}`, "error");
        return;
      }
      addToast("Exception ajoutée", "success");
      await load();
    } catch (e) {
      addToast("Erreur réseau", "error");
    }
  };

  const deleteException = async (id) => {
    try {
      const r = await fetch(`/api/agenda/exceptions/${id}`, { method: "DELETE", headers: authHeaders });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        addToast(`Échec de la suppression : ${err.error || r.status}`, "error");
        return;
      }
      addToast("Exception supprimée", "success");
      await load();
    } catch (e) {
      addToast("Erreur réseau", "error");
    }
  };

  const [detailAppt, setDetailAppt] = useState(null);

  if (loading) return <div style={{ textAlign: "center", padding: 60, color: "var(--text-muted)" }}>Chargement de l'agenda…</div>;

  return (
    <div style={{ maxWidth: 1100, margin: "0 auto" }}>

      {/* En-tête */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24 }}>
        <div>
          <h1 style={{ margin: "0 0 4px", fontSize: 22, fontWeight: 800, color: "var(--text-primary)" }}>Agenda & Prise de RDV</h1>
          <p style={{ margin: 0, fontSize: 13, color: "var(--text-muted)" }}>Gérez vos types de rendez-vous, disponibilités et réservations</p>
        </div>
        {meetingTypes.length > 0 && (
          <div style={{ display: "flex", gap: 8 }}>
            <a
              href={`/booking/${meetingTypes[0]?.slug}`}
              target="_blank"
              rel="noreferrer"
              style={{ ...btnPrimary, textDecoration: "none", display: "flex", alignItems: "center", gap: 6 }}
            >
              <Link2 size={14} /> Page publique
            </a>
          </div>
        )}
      </div>

      {/* Onglets */}
      <div style={{ display: "flex", gap: 4, borderBottom: "1px solid var(--border)", marginBottom: 24 }}>
        {TABS.map(t => {
          const { Icon } = t;
          const isActive = activeTab === t.id;
          return (
            <button
              key={t.id}
              onClick={() => setActiveTab(t.id)}
              style={{
                display: "flex", alignItems: "center", gap: 6, padding: "10px 16px",
                background: "none", border: "none", cursor: "pointer",
                color: isActive ? "#818CF8" : "var(--text-muted)",
                borderBottom: isActive ? "2px solid #6366F1" : "2px solid transparent",
                fontWeight: isActive ? 700 : 400, fontSize: 13,
                transition: "all 0.13s", marginBottom: -1,
              }}
            >
              <Icon size={14} />
              {t.label}
              {t.id === "list" && appointments.filter(a => a.status === "en_attente").length > 0 && (
                <span style={{ background: "#EF4444", color: "#fff", fontSize: 9, padding: "1px 5px", borderRadius: 8, fontWeight: 700, marginLeft: 2 }}>
                  {appointments.filter(a => a.status === "en_attente").length}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Contenu */}
      <div style={card}>
        {activeTab === "calendar" && (
          <CalendarTab appointments={appointments} onApptClick={setDetailAppt} />
        )}
        {activeTab === "list" && (
          <ListTab appointments={appointments} meetingTypes={meetingTypes} onUpdate={updateAppt} onCancel={cancelAppt} />
        )}
        {activeTab === "types" && (
          <TypesTab types={meetingTypes} onSave={saveType} onDelete={deleteType} />
        )}
        {activeTab === "avails" && (
          <AvailsTab
            availabilities={availabilities}
            exceptions={exceptions}
            onSaveDay={saveDay}
            onDeleteAvail={async (id) => {
              const r = await fetch(`/api/agenda/availabilities/${id}`, { method: "DELETE", headers: authHeaders });
              if (!r.ok) { addToast("Échec de la suppression", "error"); return; }
              load();
            }}
            onAddException={addException}
            onDeleteException={deleteException}
          />
        )}
        {activeTab === "stats" && (
          <StatsTab stats={stats} />
        )}
      </div>

      {/* Modal détail RDV depuis calendrier */}
      <Modal open={!!detailAppt} onClose={() => setDetailAppt(null)} title="Rendez-vous">
        {detailAppt && (
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
              <div style={{ width: 10, height: 10, borderRadius: "50%", background: detailAppt.type_color || "#6366F1" }} />
              <div style={{ fontWeight: 700, fontSize: 15, color: "var(--text-primary)" }}>{detailAppt.type_title}</div>
              <span style={{ background: `${(STATUS_LABELS[detailAppt.status]?.color || "#64748B")}20`, color: STATUS_LABELS[detailAppt.status]?.color || "#64748B", padding: "2px 8px", borderRadius: 6, fontSize: 11, fontWeight: 600 }}>
                {STATUS_LABELS[detailAppt.status]?.label || detailAppt.status}
              </span>
            </div>
            <div style={{ fontSize: 14, color: "var(--text-muted)", marginBottom: 8 }}>📅 {formatDT(detailAppt.start_datetime)}</div>
            <div style={{ fontSize: 14, color: "var(--text-primary)", marginBottom: 4 }}>👤 <strong>{detailAppt.guest_name}</strong></div>
            <div style={{ fontSize: 13, color: "var(--text-muted)", marginBottom: 12 }}>✉️ {detailAppt.guest_email}</div>
            {detailAppt.guest_message && <div style={{ fontSize: 13, color: "var(--text-muted)", background: "rgba(255,255,255,0.04)", borderRadius: 8, padding: "10px 14px" }}>💬 {detailAppt.guest_message}</div>}
          </div>
        )}
      </Modal>
    </div>
  );
}
